import { useEffect, useLayoutEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/Dialog';
import Spinner from '@/components/Spinner';
import VisuallyHidden from '@/components/VisuallyHidden';
import Input from '@/components/form/Input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/form/Select';
import { useShallowEqualSelector } from '@/hooks';
import { useHasActiveSubscription } from '@/hooks/user';
import Add from '@/icons/add.svg';
import { setCreateFolderModal } from '@/store/myDocuments';
import { createFolder } from '@/store/myDocuments/thunks';
import {
	setIsModalUpgradePlanOpen,
	setModalUpgradePlanScope
} from '@/store/profile';
import { cn, showError, showSuccess } from '@/utils';

export function CreateFolderButton({
	className,
	children,
	withIcon = false,
	...props
}) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const hasUserActiveSubscription = useHasActiveSubscription();

	return (
		<Button
			className={cn('gap-2', className)}
			variant={withIcon ? 'outline' : 'default'}
			{...props}
			onClick={() => {
				if (!hasUserActiveSubscription) {
					dispatch(setIsModalUpgradePlanOpen(true));
					dispatch(setModalUpgradePlanScope('save'));

					return;
				}

				dispatch(setCreateFolderModal(true));
			}}
		>
			{withIcon && <Add />}
			{children || t('myDocuments.modals.saveToFolder.createFolder.title')}
		</Button>
	);
}

export function CreateFolderModal({ open, onClose }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { folders, isLoading, isLoadingCreation } = useShallowEqualSelector(
		({ myDocuments }) => ({
			folders: myDocuments.folders || [],
			isLoading: myDocuments.fetchFolders.loading,
			isLoadingCreation: myDocuments.createFolder.loading
		})
	);

	const schema = Yup.object({
		folderName: Yup.string().max(100).required(),
		subfolder: Yup.boolean().required(),
		parentId: Yup.string().nullable()
	});
	const methods = useForm({
		resolver: yupResolver(schema),
		defaultValues: { folderName: '', subfolder: false, parentId: null },
		mode: 'onChange'
	});
	const subfolder = methods.watch('subfolder');
	const parentId = methods.watch('parentId');

	useLayoutEffect(() => {
		if (subfolder && !parentId) {
			// wait to render the select
			setTimeout(() => {
				methods.setValue('parentId', folders[0]?.id?.toString() || null);
			}, 50);
		}
	}, [subfolder, parentId]);

	useEffect(() => {
		if (open) {
			methods.reset({ folderName: '', subfolder: false, parentId: null });
		}
	}, [open]);

	const handleCreateFolder = async values => {
		dispatch(
			createFolder({
				name: values.folderName,
				parentId: values.subfolder ? Number(values.parentId) : null
			})
		)
			.unwrap()
			.then(() => {
				showSuccess(t('myDocuments.toast.folderCreateSuccess'));
				onClose(false);
			})
			.catch(error => {
				showError(error);
			});
	};

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="w-[280px] md:w-[397px]">
				<DialogHeader className="flex h-12 flex-row items-center justify-between border-b border-grey-200 px-4">
					<DialogTitle className="font-title text-xs font-semibold leading-5 md:text-sm md:leading-6">
						{t('myDocuments.modals.saveToFolder.createFolder.title')}
					</DialogTitle>
					<VisuallyHidden asChild>
						<DialogDescription>
							{t(
								'myDocuments.modals.saveToFolder.createFolder.description'
							)}
						</DialogDescription>
					</VisuallyHidden>
				</DialogHeader>

				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(handleCreateFolder)}>
						<div
							className={cn(
								'flex flex-col px-4 pt-9',
								subfolder ? 'pb-4' : 'pb-9'
							)}
						>
							<Input
								name="folderName"
								label={t(
									'myDocuments.modals.saveToFolder.createFolder.label'
								)}
								placeholder={`${t('myDocuments.modals.saveToFolder.createFolder.placeholder')}`}
								size="lg"
							/>

							{/** TODO uncomment after the MVP
							<Switch
								id="subfolder"
								label={t(
									'myDocuments.modals.saveToFolder.createFolder.includeAsSubfolder'
								)}
								name="subfolder"
								className={cn('mt-4', !folders.length && 'hidden')}
							/>
							 */}
						</div>

						{subfolder && (
							<div className="flex flex-col px-4 pb-9">
								<Select
									name="parentId"
									label={t(
										'myDocuments.modals.saveToFolder.createFolder.selectSubfolder'
									)}
								>
									<SelectTrigger className="group border-gray-200 bg-white p-0 text-grey-500 group-hover:text-grey-500">
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="relative bg-white text-grey-500">
										{isLoading && (
											<Spinner
												className="absolute inset-0 m-auto text-grey-500"
												size={24}
											/>
										)}
										{folders.map(folder => (
											<SelectItem
												className="text-grey-500 hover:bg-grey-100 data-[state=checked]:bg-grey-100 data-[state=checked]:text-black"
												key={folder.id}
												value={folder.id.toString()}
											>
												{folder.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={event => {
									event.preventDefault();
									onClose(false);
								}}
							>
								{t('common.cancel')}
							</Button>
							<Button type="submit" loading={isLoadingCreation}>
								{t('common.create')}
							</Button>
						</DialogFooter>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
}
